// src/features/excursions/hooks/useExcursionsData.js
import { useState, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { db } from "services/firebaseConfig";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  Timestamp,
  setDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";

const cleanPaymentValue = (paymentInfo) => {
  if (!paymentInfo || typeof paymentInfo !== "string") return 0;
  const numericString = paymentInfo
    .replace(/R\$\s*/, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const number = parseFloat(numericString);
  return isNaN(number) ? 0 : number;
};

export const getOccupiedCamarotes = async (date, excludeExcursionId = null) => {
  if (!date) return [];

  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  const q = query(
    collection(db, "excursions"),
    where("excursionDate", ">=", Timestamp.fromDate(startOfDay)),
    where("excursionDate", "<=", Timestamp.fromDate(endOfDay)),
  );

  const querySnapshot = await getDocs(q);
  const occupiedNumbers = [];

  querySnapshot.forEach((doc) => {
    if (doc.id === excludeExcursionId) return;

    const data = doc.data();
    if (data.camarote?.requested) {
      if (data.camarote.number) {
        occupiedNumbers.push(String(data.camarote.number).padStart(2, "0"));
      }
      if (data.camarote.numbers && Array.isArray(data.camarote.numbers)) {
        data.camarote.numbers.forEach((num) => {
          occupiedNumbers.push(String(num).padStart(2, "0"));
        });
      }
    }
  });

  return occupiedNumbers;
};

export const useExcursionsData = (selectedDate, searchTerm) => {
  const [excursions, setExcursions] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const configRef = doc(db, "config", "excursions");
    const unsubscribeConfig = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists() && typeof docSnap.data().dailyLimit !== "undefined") {
        setDailyLimit(Number(docSnap.data().dailyLimit));
      } else {
        setDailyLimit(null);
      }
    });
    return () => unsubscribeConfig();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    let q;

    if (searchTerm && searchTerm.length > 3) {
      // Global Search Mode by clientPhone
      q = query(
        collection(db, "excursions"),
        where("clientPhone", ">=", searchTerm),
        where("clientPhone", "<=", searchTerm + "\uf8ff"),
      );
    } else {
      // Date Mode
      if (!selectedDate) {
        setIsLoading(false);
        return;
      }

      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      q = query(
        collection(db, "excursions"),
        where("excursionDate", ">=", startOfDay),
        where("excursionDate", "<=", endOfDay),
      );
    }

    const unsubscribeExcursions = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedExcursions = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedExcursions.push({
            id: doc.id,
            ...data,
            excursionDate: data.excursionDate?.toDate(),
          });
        });
        fetchedExcursions.sort((a, b) =>
          a.clientName.localeCompare(b.clientName),
        );

        // Daily Data is ONLY fetched in Daily Mode
        // If we search, we may not want to fetch attendance for all different days
        // Or we just skip it.
        // For now, let's only fetch daily data if NOT searching, or we would need to fetch multiple docs.
        // Simplified requirement: just list results. We can skip daily attendance check for search results or handle later.
        // The original request implies replacing the list with search results.

        if (searchTerm && searchTerm.length > 3) {
          // In search mode, we don't fetch "dadosDiarios" for every single date returned.
          // Just return the excursions.
          setExcursions(fetchedExcursions);
          setIsLoading(false);
        } else {
          // Logic with daily data sync
          const docId = formatDateToYYYYMMDD(selectedDate);
          const docRef = doc(db, "dadosDiarios", docId);

          const unsubscribeDailyData = onSnapshot(docRef, (docSnapshot) => {
            const dailyData = docSnapshot.data() || { presence: [], lunch: {} };
            const mergedData = fetchedExcursions.map((ex) => ({
              ...ex,
              status: ex.status,
              presenceStatus: dailyData.presence.includes(ex.id)
                ? "Presente"
                : "Ausente",
              lunchInfo: dailyData.lunch[ex.id] || "",
            }));
            setExcursions(mergedData);
            setIsLoading(false);
          });
          return () => unsubscribeDailyData();
        }
      },
      (snapshotError) => {
        console.error(
          "Erro no listener do Firestore (Excursions):",
          snapshotError,
        );
        setError("Não foi possível carregar as excursões em tempo real.");
        setIsLoading(false);
      },
    );

    return () => unsubscribeExcursions();
  }, [selectedDate, searchTerm]);

  const totalClients = useMemo(() => {
    return excursions.reduce((sum, ex) => {
      if (ex.status === "Cancelado") return sum;
      return sum + (ex.quantity || 0);
    }, 0);
  }, [excursions]);

  const totalPayments = useMemo(() => {
    return excursions.reduce(
      (sum, ex) => sum + cleanPaymentValue(ex.paymentInfo),
      0,
    );
  }, [excursions]);

  const markAsPresent = async (excursionId) => {
    const docId = formatDateToYYYYMMDD(selectedDate);
    const docRef = doc(db, "dadosDiarios", docId);
    try {
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.data() || { presence: [], lunch: {} };
      const newPresence = [...new Set([...currentData.presence, excursionId])];
      await setDoc(docRef, { ...currentData, presence: newPresence });
    } catch (firestoreError) {
      console.error("Erro ao marcar presença:", firestoreError);
      toast.error("Falha ao marcar presença.");
    }
  };

  const updateLunchInfo = async (excursionId, text) => {
    const docId = formatDateToYYYYMMDD(selectedDate);
    const docRef = doc(db, "dadosDiarios", docId);
    try {
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.data() || { presence: [], lunch: {} };
      const newLunchData = { ...currentData.lunch, [excursionId]: text };
      if (!text) {
        delete newLunchData[excursionId];
      }
      await setDoc(docRef, { ...currentData, lunch: newLunchData });
    } catch (firestoreError) {
      console.error("Erro ao salvar info do almoço:", firestoreError);
      toast.error("Falha ao salvar a alteração.");
    }
  };

  const addExcursion = async (newExcursionData) => {
    try {
      const excursionDate = new Date(newExcursionData.date + "T00:00:00");
      await addDoc(collection(db, "excursions"), {
        clientName: newExcursionData.clientName,
        excursionDate: Timestamp.fromDate(excursionDate),
        city: newExcursionData.city || "",
        clientPhone: newExcursionData.clientPhone || "",
        quantity: Number(newExcursionData.quantity) || 0,
        status: newExcursionData.status || "Agendado",
        paymentInfo: "",
        vehicle: newExcursionData.vehicle || "",
        comboIncluded: newExcursionData.comboIncluded || false,
        camarote: {
          requested: newExcursionData.camaroteRequested || false,
          numbers: newExcursionData.camaroteNumbers || [],
          number: newExcursionData.camaroteNumbers?.[0] || "",
        },
        isGuide: newExcursionData.isGuide || false,
        organizerName: newExcursionData.organizerName || "",
        organizerPhone: newExcursionData.organizerPhone || "",
        createdAt: Timestamp.now(),
        source: "app_manual_firestore",
      });
      toast.success("Excursão adicionada com sucesso!");
    } catch (err) {
      toast.error(`Falha ao adicionar: ${err.message}`);
      console.error(err);
    }
  };

  const updateExcursion = async (excursionId, data) => {
    if (!excursionId) return toast.error("ID da excursão não encontrado.");
    const docRef = doc(db, "excursions", excursionId);
    try {
      const updateData = { ...data };

      if (data.comboIncluded !== undefined) {
        updateData.comboIncluded = data.comboIncluded;
      }

      if (data.camarote) {
        const requested = data.camarote.requested;
        let numbers = data.camarote.numbers || [];
        let number = data.camarote.number || "";

        if (number && numbers.length === 0) {
          numbers = [number];
        }
        if (numbers.length > 0 && !number) {
          number = numbers[0];
        }

        updateData.camarote = {
          requested,
          numbers,
          number,
        };
      }

      await updateDoc(docRef, updateData);
      toast.success("Excursão atualizada com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error(`Falha ao atualizar excursão: ${err.message}`);
    }
  };

  const deleteExcursion = async (excursionId) => {
    if (!excursionId) return toast.error("ID da excursão não encontrado.");
    if (window.confirm("Tem certeza que deseja excluir esta excursão?")) {
      try {
        await deleteDoc(doc(db, "excursions", excursionId));
        toast.success("Excursão excluída com sucesso.");
      } catch (err) {
        toast.error(`Falha ao excluir excursão: ${err.message}`);
      }
    }
  };

  return {
    excursions,
    dailyLimit,
    totalClients,
    totalPayments,
    isLoading,
    error,
    markAsPresent,
    updateLunchInfo,
    addExcursion,
    updateExcursion,
    deleteExcursion,
  };
};
